import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { colors, typography } from '../constants';

/**
 * The one spinner every Profit IQ load uses.
 *
 * Fixed to the viewport, with the circle itself at the exact center and the
 * label hung below it, so a short first load, a tall refresh, and a different
 * caption all put the indicator in the same place.
 */
export function CenteredLoader({ label }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <CircularProgress size={40} sx={{ color: colors.primary }} />
      {label ? (
        <Typography
          sx={{
            position: 'absolute',
            top: '100%',
            mt: 1,
            whiteSpace: 'nowrap',
            fontSize: typography.caption,
            fontWeight: 600,
            color: colors.mutedForeground,
          }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Loading veil drawn over content that is being refreshed.
 *
 * Saving a cost or a price re-reads the dashboard, and unmounting the screen
 * for that would collapse expanded categories, drop the scroll position and
 * blur the field mid-edit. This covers the content instead: the rows stay
 * visible and in place under a translucent layer, and the layer ignores pointer
 * events so typing and scrolling keep working while the figures settle.
 */
export function RefreshingOverlay({ visible, label = 'Updating…' }) {
  if (!visible) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        pointerEvents: 'none',
      }}
    >
      {/* Veil and spinner are siblings so the opacity dims the content behind
          without washing out the indicator itself. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: colors.background,
          opacity: 0.6,
        }}
      />
      <CenteredLoader label={label} />
    </Box>
  );
}

export function ErrorView({ message }) {
  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <WarningAmberIcon sx={{ fontSize: 28, color: colors.destructive }} />
      <Typography sx={{ mt: 1, color: colors.destructive }}>
        {message}
      </Typography>
    </Box>
  );
}

export function EmptyState({ icon, title, message, ctaLabel, onCta }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3.5,
        gap: 1.25,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: colors.accent,
          color: colors.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
        }}
      >
        {icon || <CloudUploadOutlinedIcon fontSize="inherit" />}
      </Box>
      <Typography
        sx={{
          fontSize: typography.heading,
          fontWeight: 800,
          color: colors.foreground,
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: typography.small,
          color: colors.mutedForeground,
          textAlign: 'center',
          lineHeight: 1.45,
          maxWidth: 460,
        }}
      >
        {message}
      </Typography>
      {ctaLabel && onCta ? (
        <Button
          variant="contained"
          onClick={onCta}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2.5,
            px: 2.75,
            py: 1.25,
            backgroundColor: colors.primary,
            '&:hover': { backgroundColor: colors.primary, filter: 'brightness(0.95)' },
          }}
        >
          {ctaLabel}
        </Button>
      ) : null}
    </Box>
  );
}
